import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ChangeReview from "@/components/ChangeReview";
import AdminProductDetail from "@/pages/AdminProductDetail";
import { useAuth } from "@/context/AuthContext";

const PENDING = ["draft", "submitted", "inspection_pending", "inspection_passed", "admin_review"];
const LABEL = {
  live: "Live", inspection_pending: "Inspection Pending", inspection_passed: "Inspection Passed",
  admin_review: "Admin Review", draft: "Draft", submitted: "Submitted",
  suspended: "Suspended", archived: "Archived", rejected: "Rejected",
};
const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending Approval" },
  { key: "changes", label: "Change Requests" },
  { key: "inspection_pending", label: "Inspection Pending" },
  { key: "live", label: "Live" },
  { key: "suspended", label: "Suspended" },
  { key: "archived", label: "Archived" },
];

function Badge({ s }) {
  const cls = s === "live" ? "text-[hsl(var(--accent))] border-[hsl(var(--accent))]"
    : (s === "suspended" || s === "rejected") ? "text-destructive border-destructive"
    : "text-muted-foreground border-foreground/30";
  return <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${cls}`}>{LABEL[s] || s || "—"}</span>;
}

export default function AdminProducts({ onInspect }) {
  const { user } = useAuth();
  const can = (p) => user?.admin_roles?.includes("super_admin") || (user?.permissions || []).includes(p);
  const [products, setProducts] = useState([]);
  const [changes, setChanges] = useState([]);
  const [sub, setSub] = useState("all");
  const [busy, setBusy] = useState(false);
  const [reasonFor, setReasonFor] = useState(null); // { kind:'product'|'change', action, id, title }
  const [reason, setReason] = useState("");
  const [reviewCR, setReviewCR] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    const [p, cr] = await Promise.all([
      api.get("/admin/products").then((r) => r.data),
      api.get("/admin/change-requests").then((r) => r.data).catch(() => []),
    ]);
    setProducts(p); setChanges(cr);
  };
  useEffect(() => { load(); }, []);

  const act = async (url, body) => {
    setBusy(true);
    try { await api.post(url, body || {}); toast.success("Done"); await load(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); return false; }
    finally { setBusy(false); }
  };
  const openReason = (kind, action, id, title) => { setReasonFor({ kind, action, id, title }); setReason(""); };
  const submitReason = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    const { kind, action, id } = reasonFor;
    const url = kind === "change" ? `/admin/change-requests/${id}/reject` : `/admin/products/${id}/${action}`;
    if (await act(url, { reason })) setReasonFor(null);
  };

  const count = {
    all: products.length,
    pending: products.filter((p) => PENDING.includes(p.lifecycle)).length,
    changes: changes.length,
    inspection_pending: products.filter((p) => p.lifecycle === "inspection_pending").length,
    live: products.filter((p) => p.lifecycle === "live").length,
    suspended: products.filter((p) => p.lifecycle === "suspended").length,
    archived: products.filter((p) => p.lifecycle === "archived").length,
  };
  const rows = sub === "all" ? products
    : sub === "pending" ? products.filter((p) => PENDING.includes(p.lifecycle))
    : products.filter((p) => p.lifecycle === sub);

  const Actions = ({ p }) => {
    const s = p.lifecycle;
    const canApprove = can("product.approve");
    return (
      <div className="flex gap-1.5 justify-end flex-wrap">
        {p.has_pending_change && canApprove && <button onClick={() => setSub("changes")} className="border border-[hsl(var(--accent))] text-[hsl(var(--accent))] px-2 py-1 text-[10px] font-mono uppercase">Review change</button>}
        {PENDING.includes(s) || s === "rejected" ? (
          <>
            {onInspect && can("product.inspect") && <button onClick={() => onInspect(p)} className="border border-foreground px-2 py-1 text-[10px] font-mono uppercase hover:bg-foreground hover:text-background">Inspect</button>}
            {canApprove && <button disabled={busy} onClick={() => act(`/admin/products/${p.id}/approve`)} className="bg-foreground text-background px-2 py-1 text-[10px] font-mono uppercase hover:bg-[hsl(var(--accent))] disabled:opacity-50">Approve</button>}
            {s !== "rejected" && canApprove && <button onClick={() => openReason("product", "reject", p.id, p.title)} className="border border-destructive text-destructive px-2 py-1 text-[10px] font-mono uppercase">Reject</button>}
          </>
        ) : s === "live" ? canApprove ? (
          <>
            <button onClick={() => openReason("product", "suspend", p.id, p.title)} className="border border-destructive text-destructive px-2 py-1 text-[10px] font-mono uppercase">Suspend</button>
            <button onClick={() => openReason("product", "archive", p.id, p.title)} className="border border-foreground/40 px-2 py-1 text-[10px] font-mono uppercase">Archive</button>
          </>
        ) : null : s === "suspended" ? canApprove ? (
          <>
            <button disabled={busy} onClick={() => act(`/admin/products/${p.id}/restore`)} className="bg-foreground text-background px-2 py-1 text-[10px] font-mono uppercase hover:bg-[hsl(var(--accent))]">Restore</button>
            <button onClick={() => openReason("product", "archive", p.id, p.title)} className="border border-foreground/40 px-2 py-1 text-[10px] font-mono uppercase">Archive</button>
          </>
        ) : null : s === "archived" && canApprove ? (
          <button disabled={busy} onClick={() => act(`/admin/products/${p.id}/restore`)} className="bg-foreground text-background px-2 py-1 text-[10px] font-mono uppercase hover:bg-[hsl(var(--accent))]">Restore</button>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      <div className="flex gap-5 flex-wrap border-b border-foreground/20 mb-6 font-mono text-[11px] uppercase tracking-widest">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setSub(t.key)}
            className={`py-2 border-b-2 whitespace-nowrap ${sub === t.key ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label} <span className="ml-0.5">({count[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {sub === "changes" ? (
        changes.length === 0 ? <p className="font-serif text-xl text-muted-foreground">No pending change requests.</p> : (
          <div className="space-y-3">
            {changes.map((c) => (
              <div key={c.id} className="border border-foreground p-4 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.vendor_name} · {new Date(c.created_at).toLocaleDateString("en-IN")}</p>
                  <p className="font-serif text-xl mt-1">{c.product_title}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Changed: {(c.changes || []).map((ch) => ch.field.replace(/_/g, " ")).join(", ")}
                    <span className="text-foreground"> · {(c.changes || []).length} field{(c.changes || []).length !== 1 ? "s" : ""}</span>
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {can("product.approve")
                    ? <button onClick={() => setReviewCR(c)} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase hover:bg-[hsl(var(--accent))]">Review →</button>
                    : <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground self-center">View only</span>}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest">
            <th className="text-left py-2">Product</th><th className="text-left">Vendor</th><th className="text-left">Price</th><th className="text-left">State</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-foreground/15">
                <td className="py-3">
                  <button onClick={() => setDetailId(p.id)} className="flex items-center gap-3 text-left group">
                    <div className="w-10 h-10 bg-secondary shrink-0 overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                    <span className="font-serif group-hover:text-[hsl(var(--accent))] transition-colors">{p.title}</span>
                    {p.has_pending_change && <span className="font-mono text-[8px] uppercase tracking-widest text-[hsl(var(--accent))] border border-[hsl(var(--accent))] px-1">Change pending</span>}
                  </button>
                </td>
                <td className="text-[11px]">{p.vendor_name}</td>
                <td className="font-mono text-xs">₹ {Number(p.price || 0).toLocaleString("en-IN")}</td>
                <td><Badge s={p.lifecycle} /></td>
                <td className="text-right"><Actions p={p} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-8 font-serif text-lg text-muted-foreground">Nothing here.</td></tr>}
          </tbody>
        </table>
        </div>
      )}

      {detailId && <AdminProductDetail productId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}

      {reviewCR && <ChangeReview cr={reviewCR} onClose={() => setReviewCR(null)} onDone={() => { setReviewCR(null); load(); }} />}

      {reasonFor && (
        <div className="fixed inset-0 z-[70] bg-foreground/40 flex items-center justify-center p-4" onClick={() => setReasonFor(null)}>
          <div className="bg-background border-2 border-foreground w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reason required</p>
            <h3 className="font-serif text-2xl tracking-tight mt-1 capitalize">{reasonFor.action} — {reasonFor.title}</h3>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="Why? (shown in the audit log)"
              className="w-full mt-4 border border-foreground p-3 text-sm min-h-[90px] outline-none focus:border-[hsl(var(--accent))]" />
            <div className="flex gap-3 mt-4 justify-end">
              <button onClick={() => setReasonFor(null)} className="border border-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
              <button disabled={busy || !reason.trim()} onClick={submitReason} className="bg-destructive text-destructive-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest disabled:opacity-50">Confirm {reasonFor.action}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
